import { Header } from "build-ts/metadata";
import { ScheduledJobProto, ScheduledJobRunProto } from "build-ts/schedule";
import showToast from "hooks/useCustomToast";

export function postRequest<T>(payload: T): RequestInit {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  };
}

export function getRequest(): RequestInit {
  return {
    method: "GET",
    credentials: "include",
  };
}

export function deleteRequest(): RequestInit {
  return {
    method: "DELETE",
    credentials: "include",
  };
}

export function putRequest(): RequestInit {
  return {
    method: "PUT",
    credentials: "include",
  };
}
/**
 * Create a new scheduled job.
 * 
 * @param {ScheduledJobProto} proto - The scheduled job to create.
 * @returns {Promise<ScheduledJobProto | null>} A promise that resolves to the created scheduled job data.
 * @throws {Error} Throws an error if there is an issue creating the scheduled job or if the request fails.
 */
export const putScheduledJob = async (proto: ScheduledJobProto) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_PUT_SCHEDULED_JOB!}`,
      postRequest(ScheduledJobProto.toJSON(proto))
    );

    if (!response.ok) {
      showToast("Scheduled Job Execution Failed", "", "error");
      return null;
    }

    const data = await response.json();
    return ScheduledJobProto.fromJSON(data);
  } catch (error) {
    showToast("Scheduled Job Execution Failed", "", "error");
    return null;
  }
};

/**
 * Get a scheduled job by ID.
 * 
 * @param {string} id - The ID of the scheduled job to get.
 * @returns {Promise<ScheduledJobProto | null>} A promise that resolves to the scheduled job data.
 * @throws {Error} Throws an error if there is an issue getting the scheduled job or if the request fails.
 */
export const getScheduledJob = async (id: string, includeRuns: boolean = false) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_SCHEDULE_JOB!}/${id}?includeRuns=${includeRuns}`,
      getRequest()
    );

    if (!response.ok) {
      if (response.status !== 404) {
        showToast("Scheduled Job Execution Failed", "", "error");
      }
      return null;
    }

    const data = await response.json();
    return ScheduledJobProto.fromJSON(data);
  } catch (error) {
    showToast("Scheduled Job Execution Failed", "", "error");
    return null;
  }
};

/**
 * Delete a scheduled job.
 * 
 * @param {string} id - The ID of the scheduled job to delete.
 * @returns {Promise<Response | null>} A promise that resolves to the response from the server.
 * @throws {Error} Throws an error if there is an issue deleting the scheduled job or if the request fails.
 */
export const deleteScheduledJob = async (id: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_SCHEDULE_JOB!}/${id}`,
      deleteRequest()
    );

    if (!response.ok) {
      showToast("Failed to delete scheduled job", "", "error");
      return null;
    }

    return response;
  } catch (error) {
    showToast("Failed to delete scheduled job", "", "error");
  }
};

/**
 * Toggle a scheduled job active/inactive status.
 * 
 * @param {string} id - The ID of the scheduled job to set to active/inactive.
 * @param {boolean} active - True to activate, false to deactivate.
 * @returns {Promise<Response | null>} A promise that resolves to the response from the server.
 * @throws {Error} Throws an error if there is an issue toggling the scheduled job or if the request fails.
 */
export const setScheduledJobIsActive = async (id: string, active: boolean = false) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_SET_JOB_TOGGLE_ACTIVE!}/${id}?active=${active}`,
      putRequest()
    );

    if (!response.ok) {
      // Parse the error response body
      const errorData = await response.json();
      const errorMsg = errorData.errorMsg || "";

      showToast("Failed to toggle scheduled job status", errorMsg, "error");
      return null;
    }

    return response;
  } catch (error) {
    showToast("Failed to toggle scheduled job status", "", "error");
  }
};

/**
 * Delete a scheduled job run.
 * 
 * @param {string} id - The ID of the scheduled job run to delete.
 * @returns {Promise<Response | null>} A promise that resolves to the response from the server.
 * @throws {Error} Throws an error if there is an issue deleting the scheduled job run or if the request fails.
 */
export const deleteScheduledJobRun = async (id: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_JOB_RUN!}/${id}`,
      deleteRequest()
    );

    if (!response.ok) {
      showToast("Scheduled Job Execution Failed", "", "error");
      return null;
    }

    return response;
  } catch (error) {
    showToast("Scheduled Job Execution Failed", "", "error");
  }
};

/**
 * Get scheduled job runs.
 * 
 * @param {string} id - The ID of the scheduled job to get runs for.
 * @returns {Promise<Header[]>} A promise that resolves to the scheduled job runs data.
 * @throws {Error} Throws an error if there is an issue getting the scheduled job runs or if the request fails.
 */
export const getScheduledJobRunsByJobId = async (jobId: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_LIST_JOB_RUNS!}/${jobId}`,
      getRequest()
    );

    if (!response.ok) {
      showToast("Failed to get job runs", "", "error");
      return [];
    }

    const data = await response.json();
    return data.map((run: any) => Header.fromJSON(run));
  } catch (error) {
    showToast("Failed to get job runs", "", "error");
  }
};

/**
 * Get a specific scheduled job run by ID.
 * 
 * @param {string} runId - The ID of the scheduled job run to get.
 * @returns {Promise<ScheduledJobRunProto | null>} A promise that resolves to the scheduled job run data.
 * @throws {Error} Throws an error if there is an issue getting the scheduled job run or if the request fails.
 */
export const getScheduledJobRun = async (runId: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_GET_JOB_RUN!}/${runId}`,
      {
        method: "GET",
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Failed to get job run details", "", "error");
      return null;
    }

    const data = await response.json();
    return ScheduledJobRunProto.fromJSON(data);
  } catch (error) {
    showToast("Failed to get job run details", "", "error");
    return null;
  }
};

/**
 * Run a scheduled job.
 * 
 * @param {string} id - The ID of the scheduled job to run.
 * @returns {Promise<void>} A promise that resolves to the response from the server.
 * @throws {Error} Throws an error if there is an issue running the scheduled job or if the request fails.
 */
export const runScheduledJob = async (id: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_RUN_JOB!}/${id}`,
      {
        method: "POST",
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Scheduled Job Execution Failed", "", "error");
    }
  } catch (error) {
    showToast("Scheduled Job Execution Failed", "", "error");
  }
};

/**
 * Run a job asynchronously by metadata ID and type.
 * 
 * @param {string} metadataId - The ID of the metadata to run.
 * @param {string} metadataType - The type of metadata (e.g., ANALYSIS_PLAN, CUSTOM_AGENT).
 * @returns {Promise<Response | null>} A promise that resolves to the response from the server.
 * @throws {Error} Throws an error if there is an issue running the job or if the request fails.
 */
export const runJobAsync = async (metadataId: string, metadataType: string) => {
  try {
    const queryParams = new URLSearchParams({
      metadataId,
      metadataType,
    });

    const response = await fetch(
      `${process.env.REACT_APP_RUN_JOB!}?${queryParams}`,
      {
        method: "POST",
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Job Execution Failed", "", "error");
      return null;
    }

    return response;
  } catch (error) {
    showToast("Job Execution Failed", "", "error");
    return null;
  }
};

/**
 * List scheduled jobs with optional filtering and pagination.
 * 
 * @param {string} jobType - The type of job to filter by (default: "")
 * @param {number} pageSize - The number of jobs per page (default: -1)
 * @param {number} pageNumber - The page number to retrieve (default: 1)
 * @param {string} ownerId - The ID of the owner to filter by (default: "")
 * @param {string} sortByField - The field to sort by (default: "LAST_MODIFIED")
 * @param {boolean} sortAscending - Whether to sort in ascending order (default: false)
 * @param {boolean} includeScheduleActivity - Whether to include schedule activity (default: false)
 * @param {boolean} activeSchedule - Whether to filter by active jobs (default: true)
 * @returns {Promise<Header[] | null>} A promise that resolves to the list of scheduled jobs.
 * @throws {Error} Throws an error if there is an issue getting the scheduled jobs or if the request fails.
 */
export const listScheduledJobs = async (
  jobType: string = "",
  pageSize: number = -1,
  pageNumber: number = 1,
  ownerId: string = "",
  sortByField: string = "LAST_MODIFIED",
  sortAscending: boolean = false,
  includeScheduleActivity: boolean = false,
  activeSchedule: boolean | "" = ""
) => {
  try {
    const queryParams = new URLSearchParams({
      jobType,
      pageSize: pageSize.toString(),
      pageNumber: pageNumber.toString(),
      ownerId,
      sortByField,
      sortAscending: sortAscending.toString(),
      includeScheduleActivity: includeScheduleActivity.toString(),
      activeSchedule: activeSchedule.toString(),
    });
    const response = await fetch(
      `${process.env.REACT_APP_LIST_ALL_JOBS!}?${queryParams}`,
      getRequest()
    );

    if (!response.ok) {
      showToast("Failed to fetch scheduled jobs list", "", "error");
      return null;
    }

    const data = await response.json();
    return data.map((job: any) => Header.fromJSON(job));
  } catch (error) {
    showToast("Failed to fetch scheduled jobs list", "", "error");
    return null;
  }
};

/**
 * List scheduled jobs by owner id.
 * 
 * @param {string} ownerId - The ID of the owner to filter by.
 * @returns {Promise<ScheduledJobProto[] | null>} A promise that resolves to the list of scheduled jobs.
 * @throws {Error} Throws an error if there is an issue getting the scheduled jobs or if the request fails.
 */
export const listScheduledJobsByOwnerId = async (ownerId: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_SCHEDULE_JOB!}?ownerId=${ownerId}`,
      getRequest()
    );

    if (!response.ok) {
      showToast("Failed to fetch scheduled jobs list", "", "error");
      return null;
    }

    const data = await response.json();
    return data.map((job: any) => ScheduledJobProto.fromJSON(job));
  } catch (error) {
    showToast("Failed to fetch scheduled jobs list", "", "error");
    return null;
  }
};

/**
 * Set the visibility of a scheduled job run.
 * 
 * @param {string} runId - The ID of the scheduled job run to set visibility for.
 * @param {boolean} visibility - Whether the job run should be visible (true) or hidden (false).
 * @returns {Promise<Response | null>} A promise that resolves to the response from the server.
 * @throws {Error} Throws an error if there is an issue setting the job run visibility or if the request fails.
 */
export const setScheduledJobRunVisibility = async (runId: string, visibility: boolean) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_JOB_RUN!}/${runId}?visibility=${visibility}`,
      {
        method: "PUT",
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Failed to set job run visibility", "", "error");
      return null;
    }

    return response;
  } catch (error) {
    showToast("Failed to set job run visibility", "", "error");
    return null;
  }
};